import {
  FC,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAtomValue } from 'jotai';
import classNames from 'classnames';
import type { Geometry } from 'geojson';
import type { Point } from 'types/api-responses';
import { QueryMap } from 'components/query-map/query-map';
import PolygonArea from 'components/map/polygon-area';
import { useResizeDetector } from 'react-resize-detector';
import { useCalculateMapDimensions } from 'components/map/utils';
import { createSingleCacheEntryAtom } from 'lib/atoms';
import { formatResource } from 'utils/misc';
import {
  areaGeometryCacheAtom,
  catalogMapConfigAtom,
  catalogMapRefAtom,
} from './atoms';
import { calculateGeometryBounds } from '../show/utils';
import { useFramedCamera } from './use-framed-camera';
import { useMapResize } from 'components/map/use-map-resize';
import {
  CreateAreaMapLayers,
  type CreateAreaMapLayersProps,
} from 'components/create-area-panel/create-area-map-layers';
import { CreateAreaMapOverlays } from 'components/create-area-panel/create-area-map-overlays';
import { useAddVertexPoint } from 'components/create-area-panel/hooks';
import { workspaceDrawShapeAtom } from 'components/create-area-panel/atoms';
import { EMPTY_PICKED_AREAS } from 'components/create-area-panel/utils';
import styles from './areas-catalog-map.module.scss';

/**
 * Geometry is fetched per area, so the map draws at most this many at once.
 * Beyond it the extras are reported rather than silently dropped.
 */
export const MAX_MAPPED_AREAS = 25;

/** Matches the area colour used on the detail page. */
const AREA_COLOR = '#EB6200';

type Bounds = [Point, Point];

/**
 * Merges area bounds into one box covering all of them.
 *
 * Bounds are encoded as `[[minLng, maxLat], [maxLng, minLat]]`, matching
 * `calculateGeometryBounds`.
 *
 * @param {Bounds[]} boxes - Per-area bounds.
 * @return {Bounds | undefined} The combined box, or undefined when empty.
 */
const combineBounds = (boxes: Bounds[]): Bounds | undefined => {
  if (boxes.length === 0) {
    return undefined;
  }

  return boxes.reduce((acc, [[minLng, maxLat], [maxLng, minLat]]) => {
    const [[accMinLng, accMaxLat], [accMaxLng, accMinLat]] = acc;
    return [
      [Math.min(accMinLng, minLng), Math.max(accMaxLat, maxLat)],
      [Math.max(accMaxLng, maxLng), Math.min(accMinLat, minLat)],
    ];
  });
};

interface AreaGeometryLoaderProps {
  id: string;
  onLoaded: (
    id: string,
    geometry: Geometry,
    displayColor: string | null
  ) => void;
}

/**
 * Fetches one area's geometry and hands it up. A sibling of the map rather than a
 * child, so fetching doesn't depend on mapbox initialising — it won't in jsdom.
 *
 * @param {AreaGeometryLoaderProps} props - The area id and load callback.
 * @return {null} Always renders nothing.
 */
const AreaGeometryLoader: FC<AreaGeometryLoaderProps> = ({ id, onLoaded }) => {
  // Cached by href: unticking a row unmounts this loader, and a plain resource
  // atom would refetch the same geometry the moment the row is ticked again.
  const areaAtom = useMemo(
    () =>
      createSingleCacheEntryAtom(areaGeometryCacheAtom)(
        `/area_geometries/${id}`
      ),
    [id]
  );
  const area = formatResource(useAtomValue(areaAtom)).data;
  const geometry = area?.geometry;
  const displayColor = area?.displayColor ?? null;

  useEffect(() => {
    if (geometry) {
      onLoaded(id, geometry, displayColor);
    }
  }, [id, geometry, displayColor, onLoaded]);

  return null;
};

interface AreasCatalogMapProps extends Omit<
  CreateAreaMapLayersProps,
  'drawShape'
> {
  /**
   * Areas to draw. Checking rows draws all of them; with nothing checked, the
   * clicked row's area is drawn on its own.
   */
  ids: string[];
  /**
   * Grows the map to 80vh. Set while a creation method uses it as a workspace,
   * so there is room to draw a boundary or pick survey sections.
   */
  tall?: boolean;
  /**
   * Height as a percentage of the table shell, set once the divider under the
   * map has been dragged. Overrides the default and `tall` heights; omit to
   * leave the map at whichever of those applies.
   */
  heightPercent?: number | null;
  /** Receives the map's outer box, so a host can size it against the shell. */
  paneRef?: MutableRefObject<HTMLDivElement | null>;
}

/**
 * Catalog map, shown between the toolbar and the table. It can't draw every
 * filtered row: the catalog payload carries no geometry, and the shared Focus
 * Areas tiles serve only areas that are shared, active and not Units.
 *
 * @param {AreasCatalogMapProps} props - The component props.
 * @return {JSX.Element} The map panel.
 */
export const AreasCatalogMap: FC<AreasCatalogMapProps> = ({
  ids,
  drawnPolygons,
  onDrawnPolygonsChange,
  drawing = false,
  onDrawingChange,
  editingIndex,
  onEditingIndexChange,
  previewGeometry,
  previewColor = null,
  onUnitClick,
  onSurveyAreaPick,
  pickedSurveyAreas = EMPTY_PICKED_AREAS,
  snapToGrid = false,
  tall = false,
  heightPercent = null,
  paneRef,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // One element, two owners: the hooks below measure it, and the host sizes it.
  const setContainer = useCallback(
    (element: HTMLDivElement | null) => {
      containerRef.current = element;
      if (paneRef) {
        paneRef.current = element;
      }
    },
    [paneRef]
  );
  const [geometries, setGeometries] = useState<Record<string, Geometry>>({});
  const [colors, setColors] = useState<Record<string, string>>({});

  const drawn = useMemo(() => ids.slice(0, MAX_MAPPED_AREAS), [ids]);

  const handleLoaded = useCallback(
    (id: string, geometry: Geometry, displayColor: string | null) => {
      setGeometries((current) => {
        return current[id] ? current : { ...current, [id]: geometry };
      });
      if (displayColor) {
        setColors((current) => {
          return current[id] ? current : { ...current, [id]: displayColor };
        });
      }
    },
    []
  );

  // Frame on everything currently drawn, the preview included — a bounding-hull
  // preview can reach past its sources. Areas that haven't loaded yet simply
  // aren't in the box, so it tightens as they arrive.
  const points = useMemo(
    () =>
      combineBounds(
        [...drawn.map((id) => geometries[id]), previewGeometry]
          .filter((geometry): geometry is Geometry => !!geometry)
          .map((geometry) => calculateGeometryBounds(geometry))
          .filter((box): box is Bounds => !!box)
      ),
    [drawn, geometries, previewGeometry]
  );

  // Hold the last box we had something to frame. Emptying the selection would
  // otherwise fall back to the whole-US view — below the zoom the survey tile is
  // served at, so the next click would land on nothing.
  const lastPointsRef = useRef<Bounds | undefined>(undefined);
  if (points) {
    lastPointsRef.current = points;
  }

  const framed = useCalculateMapDimensions({
    points: points ?? lastPointsRef.current,
    // The shared hook types its ref as holding null; it only ever reads
    // `.current`, so a ref that does hold the element is safe to hand it.
    containerRef: containerRef as MutableRefObject<null>,
  });

  // What is being framed, as a value: the camera itself is recomputed on every
  // resize, and the shared `Map` moves whenever the numbers change.
  const framedBox = points ?? lastPointsRef.current;
  const frameKey = framedBox ? JSON.stringify(framedBox) : 'none';

  // Choosing a creation method grows the map, and re-fitting to the taller
  // viewport moved the view out from under the user (UAT 17), so the camera only
  // follows the subject.
  const { height: measuredHeight } = useResizeDetector({
    targetRef: containerRef,
    handleHeight: true,
    handleWidth: false,
  });
  const { latitude, longitude, zoom } = useFramedCamera(
    framed,
    frameKey,
    !!measuredHeight
  );

  // The camera the map opened with — the framed camera is not passed down as
  // live props. The shared `Map` answers camera props with an instant `jumpTo`,
  // and on this page that jump can fire from inside the map's own render cycle
  // (React 17 renders synchronously out of map event listeners, and react-map-gl
  // has the transform swapped out in that window) — the jump then lands on the
  // wrong transform and the view snaps out to the whole-US default. Framing is
  // driven imperatively below instead, like the Reset zoom button.
  const [initialCamera] = useState(() => ({ latitude, longitude, zoom }));

  // Growing the map for a drawing method resizes the container but not the
  // window, so the canvas has to be told.
  const mapRef = useAtomValue(catalogMapRefAtom);
  useMapResize(containerRef, mapRef);

  // The "click to add a vertex" tip, which the container reads for its cursor.
  const addVertexPoint = useAddVertexPoint(mapRef);

  // The panel's chosen shape, polygon or circle, is drawn here.
  const drawShape = useAtomValue(workspaceDrawShapeAtom);

  // Follow the framed subject: a survey row resolving, areas being checked, a
  // preview arriving all glide the map to what they describe. Deferred a tick
  // to land outside the render cycle described above.
  useEffect(() => {
    if (!mapRef) {
      return undefined;
    }
    const glide = window.setTimeout(
      () => mapRef.easeTo({ center: [longitude, latitude], zoom: zoom - 1 }),
      0
    );
    return () => window.clearTimeout(glide);
  }, [mapRef, longitude, latitude, zoom]);

  // Back to the framed view — what is drawn now, or the default extent when
  // nothing is. The map is only framed on mount and when the set changes, so
  // this is the way back after panning around.
  const handleResetZoom = useCallback(() => {
    mapRef?.easeTo({ center: [longitude, latitude], zoom: zoom - 1 });
  }, [mapRef, longitude, latitude, zoom]);
  const overflow = ids.length - drawn.length;

  return (
    <div
      className={classNames(styles.container, {
        [styles.containerTall]: tall,
        [styles.containerResized]: heightPercent !== null,
        // While the add-vertex tip is up, the pointer is a click, not a grab.
        [styles.addVertexHover]:
          editingIndex !== null &&
          editingIndex !== undefined &&
          addVertexPoint !== null,
        // The pencil shows the instant drawing is armed — MapboxDraw queues
        // its own cursor class until a mousemove reaches the canvas, so the
        // map kept the pan hand while already accepting draw clicks.
        [styles.drawArmed]: drawing,
      })}
      style={
        heightPercent === null
          ? undefined
          : { flexBasis: `${heightPercent}%`, height: `${heightPercent}%` }
      }
      ref={setContainer}
    >
      {drawn.map((id) => (
        <AreaGeometryLoader key={id} id={id} onLoaded={handleLoaded} />
      ))}
      <QueryMap
        className={styles.map}
        latitude={initialCamera.latitude}
        longitude={initialCamera.longitude}
        zoom={initialCamera.zoom - 1}
        mapConfigAtom={catalogMapConfigAtom}
        refAtom={catalogMapRefAtom}
        showControls
        showMapFilters
        onResetZoom={handleResetZoom}
      >
        {drawn.map((id) => {
          const geometry = geometries[id];
          return geometry ? (
            <PolygonArea
              key={id}
              id={id}
              geometry={geometry}
              color={colors[id] ?? AREA_COLOR}
            />
          ) : null;
        })}
        <CreateAreaMapLayers
          drawnPolygons={drawnPolygons}
          onDrawnPolygonsChange={onDrawnPolygonsChange}
          drawing={drawing}
          onDrawingChange={onDrawingChange}
          editingIndex={editingIndex}
          onEditingIndexChange={onEditingIndexChange}
          previewGeometry={previewGeometry}
          previewColor={previewColor}
          onUnitClick={onUnitClick}
          onSurveyAreaPick={onSurveyAreaPick}
          pickedSurveyAreas={pickedSurveyAreas}
          snapToGrid={snapToGrid}
          drawShape={drawShape}
        />
      </QueryMap>
      <CreateAreaMapOverlays
        mapRef={mapRef}
        editingIndex={editingIndex}
        addVertexPoint={addVertexPoint}
        surveyPicking={!!onSurveyAreaPick}
        snapToGrid={snapToGrid}
        workspace={tall}
      />
      {overflow > 0 && (
        <div className={styles.overflow}>
          Showing {drawn.length} of {ids.length} selected areas.
        </div>
      )}
    </div>
  );
};

export default AreasCatalogMap;
